/**
 * Плитка ленты результатов: по образцу `Tile` ленты примерок (110 px, пропорция
 * по размеру, плитки-статусы), но без пересъёмки; картинка ведёт в карточку,
 * внизу — значки соцсетей, где изображение опубликовано.
 *
 * Видео — клип (`kind === "clip"`) или загруженное (`video_ms` есть): постер (кадр 0 с) со значком ▶ и
 * длительностью; сам mp4 в `<img>` не ставится никогда — смотрят его в карточке.
 *
 * Отметка — как в окне «Сравнить»: дизлайк «Брак» (левый верх) и метка этапа (правый верх: «В улучшение» / «В публикацию» / «Утверждено» по виду),
 * повтор снимает. Шаг цепочки подписан: «доработка» / «улучшено» / «пересъёмка».
 *
 * Выбор для сравнения (`onSelect`): галочка внизу слева на картинке, вне ссылки; выбранная плитка — рамка акцентом.
 */
import clsx from "clsx";
import { Check, Heart, HeartOff, Play, Send, Sparkles } from "lucide-react";
import { Link } from "react-router";

import { mediaUrl } from "@/lib/media";
import { usd } from "@/lib/project";
import { tileCaption, tileTitle } from "@/lib/editRuns";
import { STEP_LABEL } from "@/lib/resultChains";
import { NETWORK_LABEL, resultPath, type ResultGen } from "@/lib/results";
import { likeState } from "@/lib/triptych";
import { markLabel, STAGE_HINT, STAGE_LABEL, stageOf } from "@/lib/verdict";

const STAGE_ICON = { enhance: Sparkles, publish: Send, approve: Heart } as const;

const chip = "mb-0.5 mr-0.5 inline-block rounded-[var(--radius-ui)] border border-border bg-bg px-1 text-[9.5px] text-fg-muted";

export function ResultTile({
  g,
  onPick,
  picking,
  fill = false,
  selected = false,
  onSelect,
  selectDisabled = false,
}: {
  g: ResultGen;
  onPick: (variantId: number, status: "picked" | "rejected") => void;
  picking: boolean;
  /** ширина по ячейке сетки («В публикацию» плиткой) вместо 110 px */
  fill?: boolean;
  /** плитка выбрана для сравнения */
  selected?: boolean;
  /** есть — на плитке галочка выбора; без него плитка как раньше */
  onSelect?: (variantId: number) => void;
  /** набрано максимум: невыбранные галочки неактивны */
  selectDisabled?: boolean;
}) {
  const { liked: isPicked, disliked: rejected } = likeState(g.pick);
  const round = "absolute grid size-6 place-items-center rounded-full backdrop-blur disabled:opacity-60";
  const heart = clsx(round, "top-1.5 bg-black/35");
  const stage = stageOf(g.kind);
  const StageIcon = STAGE_ICON[stage];
  const mark = markLabel(g.kind, g.pick);
  const waiting = g.status === "queued" || g.status === "running";
  const vid = g.variant_id;
  const aspect = g.size === "1024x1536" ? "aspect-[2/3]" : "aspect-[9/16]";
  const clip = g.kind === "clip" || (g.video_ms ?? null) !== null;
  const caption = clip ? g.short_model : tileCaption(g);

  return (
    <div
      className={clsx(
        "relative flex-none rounded-[var(--radius-ui)] border bg-bg p-1",
        fill ? "w-full" : "w-[110px]",
        isPicked || selected ? "border-accent ring-1 ring-accent" : "border-border",
      )}
      title={clip ? `${g.generation_model} · ${g.size || "видео"}` : tileTitle(g)}
    >
      {vid !== null && g.asset_path ? (
        <div className="relative">
          {clip ? (
            <Link
              to={resultPath(vid)}
              className="relative block aspect-[9/16] w-full overflow-hidden rounded-[4px] bg-black"
              title="видео — смотреть в карточке"
            >
              {g.poster_path ? (
                <img src={mediaUrl(g.poster_path)} alt={caption} loading="lazy" className="size-full object-cover" />
              ) : null}
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid size-7 place-items-center rounded-full bg-bg/80 text-fg">
                  <Play size={13} fill="currentColor" />
                </span>
              </span>
              {g.video_ms ? (
                <span className="absolute bottom-1 right-1 rounded-[3px] bg-black/70 px-1 text-[10px] text-white tnum">
                  {(g.video_ms / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} с
                </span>
              ) : null}
            </Link>
          ) : (
            <Link to={resultPath(vid)}>
              <img
                src={mediaUrl(g.asset_path)}
                alt={caption}
                loading="lazy"
                className={clsx(aspect, "w-full rounded-[4px] object-cover", rejected && "opacity-50")}
              />
            </Link>
          )}
          <button
            type="button"
            className={clsx(heart, "left-1.5", rejected ? "text-err" : "text-white")}
            aria-pressed={rejected}
            title={rejected ? "снять «Брак»" : "Брак"}
            aria-label="Брак"
            disabled={picking}
            onClick={() => onPick(vid, "rejected")}
          >
            <HeartOff size={13} className={rejected ? "fill-current" : undefined} />
          </button>
          <button
            type="button"
            className={clsx(heart, "right-1.5", isPicked ? "text-accent" : "text-white")}
            aria-pressed={isPicked}
            title={isPicked ? `снять «${STAGE_LABEL[stage]}»` : STAGE_HINT[stage]}
            aria-label={STAGE_LABEL[stage]}
            disabled={picking}
            onClick={() => onPick(vid, "picked")}
          >
            <StageIcon size={13} className={isPicked ? "fill-current" : undefined} />
          </button>
          {onSelect ? (
            <button
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label="Выбрать для сравнения"
              title={selectDisabled && !selected ? "не больше 4" : undefined}
              disabled={selectDisabled && !selected}
              className={clsx(round, "bottom-1.5 left-1.5", selected ? "bg-accent text-white" : "bg-black/35 text-white ring-1 ring-white/80")}
              onClick={() => onSelect(vid)}
            >
              {selected ? <Check size={13} /> : null}
            </button>
          ) : null}
        </div>
      ) : waiting ? (
        <div
          className={clsx(aspect, "grid w-full place-items-center rounded-[4px] bg-warn-bg text-[18px] text-warn")}
          title={g.status === "queued" ? "в очереди" : "генерируется"}
        >
          <span className={g.status === "running" ? "animate-pulse" : ""}>{g.status === "queued" ? "…" : "◐"}</span>
        </div>
      ) : g.status === "failed" ? (
        <div
          className={clsx(aspect, "grid w-full place-items-center rounded-[4px] bg-err-bg p-1 text-center text-err")}
          title={g.generation_error ?? "ошибка"}
        >
          <div>
            <div className="text-[18px] font-semibold">!</div>
            <div className="line-clamp-4 text-[9.5px] leading-tight break-words">{g.generation_error ?? "ошибка"}</div>
          </div>
        </div>
      ) : (
        <div
          className={clsx(aspect, "grid w-full place-items-center rounded-[4px] bg-bg-elevated text-[11px] text-fg-faint")}
        >
          {g.status}
        </div>
      )}
      <div className="mt-1 text-[10.5px] leading-tight text-fg-faint">
        <div className="truncate">{caption}</div>
        {g.cost_each !== null ? (
          <b className="tnum font-medium text-fg-muted">{usd(g.cost_each)}</b>
        ) : waiting ? (
          <b className="tnum font-medium text-warn">~{usd(g.cost_each_estimate ?? 0)}</b>
        ) : null}
        {g.chain_step ? <span className={chip}>{STEP_LABEL[g.chain_step]}</span> : null}
        {mark ? <span className={chip}>{mark.toLowerCase()}</span> : null}
        {g.posts.length > 0 ? (
          <div className="mt-0.5 flex flex-wrap gap-0.5" data-annot="публикации">
            {g.posts.map((code) => (
              <span
                key={code}
                title={code}
                className="rounded-[3px] bg-bg-elevated px-1 text-[9.5px] font-medium text-fg-muted"
              >
                {NETWORK_LABEL[code] ?? code}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
